/**
 * Figma reserves node 0:0 for the document root and 0:1 for the document's
 * first canvas (page), and appends ?node-id=0-1 when you copy a FILE link
 * from the address bar. A URL carrying one of these addresses the file, not
 * a frame inside it. Other 0:N ids are NOT canvas nodes (old files assign
 * 0:N to frames created in the file's first session), so only these two are
 * file-scoped.
 */
const FIGMA_DOCUMENT_SCOPE_NODE_IDS = new Set(['0:0', '0:1']);
/**
 * Classify a parsed Figma URL as file- or frame-scoped from its node id in
 * API form (":"-separated, e.g. "150:29" — URL "-" already normalized).
 * Shared by the dashboard and server URL parsers so the two cannot drift.
 */
export function figmaLinkTypeForNodeId(nodeId) {
    if (!nodeId || FIGMA_DOCUMENT_SCOPE_NODE_IDS.has(nodeId)) {
        return 'file';
    }
    return 'frame';
}
/**
 * figma.com path roots that address a design ARTIFACT (a document opened in
 * Figma's editor or viewer) rather than a web page. Published Figma Sites
 * live on figma.site and are the SPA list's job (services/spaDomains.ts),
 * not this one; figma.com marketing/community pages are real pages and stay
 * out of this set on purpose.
 */
const FIGMA_DESIGN_ARTIFACT_PATH_ROOTS = new Set([
    'file',
    'design',
    'proto',
    'board',
    'slides',
    'deck',
    'make',
]);
/**
 * True when a URL addresses a Figma design artifact (figma.com/design/...,
 * /file/, /proto/, ...). Such a link can NEVER render as a reviewable page:
 * figma.com answers a plain fetch HTTP 200 with a nonce-scripted app shell
 * that cannot boot inside the review proxy, so nothing downstream detects a
 * failure and the reviewer gets a permanent blank canvas (incident
 * 2026-08-12, client_reviews bd600cd9). Review creation refuses these at the
 * boundary instead; shared so the dashboard preflight and the server guards
 * cannot drift.
 */
export function isFigmaDesignArtifactUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return false;
    }
    const host = parsed.hostname.toLowerCase();
    if (host !== 'figma.com' && !host.endsWith('.figma.com')) {
        return false;
    }
    const root = parsed.pathname.split('/').find((segment) => segment.length > 0)?.toLowerCase();
    return root !== undefined && FIGMA_DESIGN_ARTIFACT_PATH_ROOTS.has(root);
}
/**
 * The refusal copy for a design-artifact review target. One string for the
 * dashboard field notices and the server 400s so the surfaces cannot drift.
 * No-break spaces bind "run Visual Comparison" so the modals never wrap
 * a 1-2 word orphan line.
 */
export const FIGMA_DESIGN_LINK_REVIEW_ERROR = 'This links to a Figma design, not a live web page. A review needs the URL of the page you built. To compare that page against the design, link the design in your project and run Visual Comparison.';
/**
 * Normalize a raw ?node-id= query value to Figma's API form: percent-decode
 * (our own pin-save emits node-id=150%3A29 via encodeURIComponent), then
 * convert URL dashes to colons — but only when no colon is present, so an
 * already-decoded id is not double-normalized. Mirrors the extension's
 * normalizeNodeId (figma-url-utils.ts); a malformed escape falls back to the
 * raw value rather than throwing.
 */
export function normalizeFigmaNodeIdParam(raw) {
    if (!raw) {
        return undefined;
    }
    let decoded;
    try {
        decoded = decodeURIComponent(raw);
    }
    catch {
        decoded = raw;
    }
    return decoded.includes(':') ? decoded : decoded.replace(/-/g, ':');
}
/**
 * The node id a project PERSISTS from a pasted link: a real frame id in API
 * form, or null when the link is file-scoped (no id, or the canvas ids a
 * copied FILE link carries). One decision for the dashboard modals, the
 * settings card, the extension's link form and the server, so a project
 * linked from a frame URL opens its picker on that frame everywhere
 * (2026-09-10 P0).
 */
export function linkedFrameNodeId(nodeId) {
    if (!nodeId)
        return null;
    return figmaLinkTypeForNodeId(nodeId) === 'frame' ? nodeId : null;
}
/**
 * The inverse of normalizeFigmaNodeIdParam: API form ("150:29") back to the
 * ?node-id= form a Figma link carries ("150-29"), so a stored frame can be
 * rebuilt into the URL the user pasted when they come back to edit it.
 */
export function figmaNodeIdUrlParam(nodeId) {
    return nodeId.replace(/:/g, '-');
}
//# sourceMappingURL=figmaLinkType.js.map